import {
  isChannelDeliveryStatus,
  OUTBOUND_CHANNEL_TYPES,
  type ChannelDeliveryStatus,
} from '@/components/tickets/MessageChannelDeliveryStatus'
import type {
  TicketDetailAttachment,
  TicketDetailMessage,
  TicketDetailQuotedMessage,
  WorkspaceUserRow,
} from '@/types/api'

export function isAssignmentSystemPseudoMessage(m: TicketDetailMessage): boolean {
  return m.id.startsWith('timeline-sys:') || m.id.startsWith('assignment-sys:')
}

export function displayUser(u: WorkspaceUserRow): string {
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  if (n) return n
  return u.email || u.id
}

export function workspaceUserInitials(u: WorkspaceUserRow): string {
  const a = u.first_name?.trim()?.[0]
  const b = u.last_name?.trim()?.[0]
  const c = u.email?.trim()?.[0]
  const s = (a && b ? `${a}${b}` : a || b || c || '?').toUpperCase()
  return s.slice(0, 2)
}

export function formatAttachmentFileSize(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return ''
  let v = Number(n)
  const units = ['Б', 'КБ', 'МБ', 'ГБ']
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  const shown = i === 0 ? Math.round(v) : v < 10 ? Math.round(v * 10) / 10 : Math.round(v)
  return `${shown} ${units[i]}`
}

export function isVideoNoteAttachment(a: TicketDetailAttachment): boolean {
  const name = (a.original_filename || a.filename || '').toLowerCase()
  return name.includes('video_note')
}

export function formatAudioDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(Number(seconds)) || Number(seconds) < 0) return ''
  const s = Math.floor(Number(seconds))
  const m = Math.floor(s / 60)
  const r = s % 60
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `0:${String(r).padStart(2, '0')}`
}

export type MessageVisualKind = 'customer' | 'operator' | 'ai' | 'internal' | 'internal_ai' | 'system'

const OPERATOR_HANDOFF_HINT_MARKER = 'передай оператору'

export function isOperatorHandoffHintMessage(m: TicketDetailMessage): boolean {
  const t = (m.message_type || '').toLowerCase()
  const sender = (m.sender_name || '').trim()
  if (t === 'system' && sender === 'Системное') return true
  const content = (m.content || '').toLowerCase()
  if (
    t === 'operator' &&
    sender === 'AI Assistant' &&
    content.includes(OPERATOR_HANDOFF_HINT_MARKER)
  ) {
    return true
  }
  return false
}

export function isAiAssistantMessage(m: TicketDetailMessage, users: WorkspaceUserRow[]): boolean {
  if (isOperatorHandoffHintMessage(m)) return false
  if ((m.sender_name || '').trim() === 'AI Assistant') return true
  if (!m.user_id) return false
  const u = users.find((x) => x.id === m.user_id)
  return Boolean((u?.email || '').toLowerCase().includes('ai-assistant'))
}

export function getMessageVisualKind(
  m: TicketDetailMessage,
  users: WorkspaceUserRow[],
): MessageVisualKind {
  const t = (m.message_type || '').toLowerCase()
  const internal = Boolean(m.is_internal) || t === 'internal'
  const ai = isAiAssistantMessage(m, users)
  if (t === 'system' || isOperatorHandoffHintMessage(m)) return 'system'
  if (internal && ai) return 'internal_ai'
  if (internal) return 'internal'
  if (t === 'customer') return 'customer'
  if (ai) return 'ai'
  return 'operator'
}

export function resolveQuotedForMessage(
  m: TicketDetailMessage,
  byId: Map<string, TicketDetailMessage>,
): TicketDetailQuotedMessage | null {
  if (m.quoted_message) return m.quoted_message
  if (!m.quote_message_id) return null
  const found = byId.get(m.quote_message_id)
  if (!found) return null
  return {
    id: found.id,
    content: found.content,
    sender_name: found.sender_name,
    sender_email: found.sender_email,
    message_type: found.message_type,
    created_at: found.created_at,
    attachments: found.attachments,
  }
}

export function quotePreviewAuthorLabel(message: TicketDetailMessage): string {
  let label = message.sender_name?.trim() || 'Пользователь'
  if (message.message_type === 'operator') {
    label = 'Оператор писал'
  } else if (message.message_type === 'customer') {
    label = 'Вы писали'
  }
  return label
}

export function quoteInBubbleAuthorLabel(
  outer: TicketDetailMessage,
  quoted: Pick<TicketDetailQuotedMessage, 'message_type' | 'sender_name'>,
): string {
  let quoteAuthorLabel = quoted.sender_name?.trim() || 'Пользователь'
  if (outer.message_type === 'operator') {
    if (quoted.message_type === 'operator') {
      quoteAuthorLabel = 'Оператор писал'
    } else if (quoted.message_type === 'customer') {
      quoteAuthorLabel = 'Вы писали'
    }
  }
  return quoteAuthorLabel
}

export const CLASSIC_MSG_TEXT = '#111111'
export const CLASSIC_MSG_TEXT_DARK_ON_BUBBLE = '#FFFFFF'
export const CLASSIC_MSG_SHADOW_DARK =
  '0 0 2px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(0, 0, 0, 0.7)'
export const CLASSIC_MSG_TIME_LIGHT = '#8B9BA8'
export const CLASSIC_MSG_TIME_DARK = '#8B8D92'

export const MESSAGE_CLASSIC: Record<
  MessageVisualKind,
  {
    label: string
    badge: string
    align: 'flex-start' | 'flex-end'
    bgLight: string
    bgDark: string
    borderLight: string
    borderDark: string
    darkTextBlackOnBubble: boolean
    boxShadowDark?: string
    quoteStripeLight: string
    quoteStripeDark: string
  }
> = {
  customer: {
    label: 'Клиент',
    badge: 'Клиент',
    align: 'flex-start',
    bgLight: '#E8F2EB',
    bgDark: '#2A3D35',
    borderLight: 'rgba(0, 0, 0, 0.1)',
    borderDark: 'rgba(255, 255, 255, 0.1)',
    darkTextBlackOnBubble: false,
    quoteStripeLight: 'rgba(0, 0, 0, 0.22)',
    quoteStripeDark: 'rgba(255, 255, 255, 0.35)',
  },
  operator: {
    label: 'Оператор',
    badge: 'Оператор',
    align: 'flex-end',
    bgLight: '#EFECE6',
    bgDark: '#3D3A35',
    borderLight: 'rgba(0, 0, 0, 0.1)',
    borderDark: 'rgba(255, 255, 255, 0.1)',
    darkTextBlackOnBubble: false,
    quoteStripeLight: 'rgba(0, 0, 0, 0.22)',
    quoteStripeDark: 'rgba(255, 255, 255, 0.35)',
  },
  ai: {
    label: 'Мозгобот',
    badge: 'Мозгобот',
    align: 'flex-end',
    bgLight: '#f1ddd0',
    bgDark: '#4A3A2F',
    borderLight: 'rgba(0, 0, 0, 0.1)',
    borderDark: 'rgba(255, 255, 255, 0.1)',
    darkTextBlackOnBubble: false,
    quoteStripeLight: 'rgba(0, 0, 0, 0.22)',
    quoteStripeDark: 'rgba(255, 255, 255, 0.35)',
  },
  internal: {
    label: 'Внутренняя заметка',
    badge: 'Заметка',
    align: 'flex-end',
    bgLight: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    bgDark: 'linear-gradient(135deg, #E8D5A3 0%, #D4C19A 100%)',
    borderLight: '#F5B84F',
    borderDark: '#B4B6BA',
    darkTextBlackOnBubble: true,
    boxShadowDark: '0 2px 6px rgba(0, 0, 0, 0.4)',
    quoteStripeLight: '#F5B84F',
    quoteStripeDark: '#B4B6BA',
  },
  internal_ai: {
    label: 'Мозгобот · заметка',
    badge: 'Мозгобот',
    align: 'flex-end',
    bgLight: '#f1ddd0',
    bgDark: '#4A3A2F',
    borderLight: 'rgba(0, 0, 0, 0.1)',
    borderDark: 'rgba(255, 255, 255, 0.1)',
    darkTextBlackOnBubble: false,
    quoteStripeLight: 'rgba(0, 0, 0, 0.22)',
    quoteStripeDark: 'rgba(255, 255, 255, 0.35)',
  },
  system: {
    label: 'Система',
    badge: 'Система',
    align: 'flex-end',
    bgLight: '#EFECE6',
    bgDark: '#3D3A35',
    borderLight: 'rgba(0, 0, 0, 0.1)',
    borderDark: 'rgba(255, 255, 255, 0.1)',
    darkTextBlackOnBubble: false,
    quoteStripeLight: 'rgba(0, 0, 0, 0.22)',
    quoteStripeDark: 'rgba(255, 255, 255, 0.35)',
  },
}

export const MESSAGE_BADGE_COLOR: Record<MessageVisualKind, string> = {
  customer: 'orange',
  operator: 'blue',
  ai: 'violet',
  internal: 'yellow',
  internal_ai: 'grape',
  system: 'teal',
}

export function initialsFromSenderFallback(name?: string | null, email?: string | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    const s = `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
    if (s) return s.slice(0, 2)
  }
  if (parts.length === 1 && parts[0].length > 0) return parts[0].slice(0, 2).toUpperCase()
  const c = email?.trim()?.[0]
  return (c || 'О').toUpperCase()
}

export const LOCALE_LABELS: Record<string, string> = {
  en: 'английский',
  tr: 'турецкий',
  ru: 'русский',
}

export function translationBlockForMessage(
  m: TicketDetailMessage,
): { label: string; text: string } | null {
  const content = (m.content || '').trim()
  const mt = (m.message_type || '').toLowerCase()
  if (mt === 'customer') {
    const tr = (m.content_translated || '').trim()
    if (tr && tr !== content) {
      return { label: 'Перевод (для оператора)', text: tr }
    }
    return null
  }
  if (mt === 'operator') {
    const ob = (m.outbound_content || '').trim()
    const tr = (m.content_translated || '').trim()
    const clientText = ob && ob !== content ? ob : tr && tr !== content ? tr : ''
    if (clientText) {
      return { label: 'Текст для клиента (перевод)', text: clientText }
    }
  }
  return null
}

export function shouldShowChannelDeliveryStatus(
  m: TicketDetailMessage,
  kind: MessageVisualKind,
): boolean {
  if (m.is_internal) return false
  if (kind !== 'operator' && kind !== 'ai') return false
  const ct = (m.channel_type || '').toLowerCase()
  if (ct && !OUTBOUND_CHANNEL_TYPES.has(ct)) return false
  return true
}

export function resolveDisplayDeliveryStatus(m: TicketDetailMessage): ChannelDeliveryStatus | null {
  if (isChannelDeliveryStatus(m.channel_delivery_status)) {
    return m.channel_delivery_status
  }
  return null
}
